import React, { useEffect, useMemo, useState } from 'react';
import {
  View, StyleSheet, Modal, Pressable, ScrollView, useWindowDimensions,
} from 'react-native';
// CORE-1751 — KAV из keyboard-controller: RN-овский внутри Modal на Android не
// работает (CORE-944), а окно теперь открывается и в приложении. На вебе он
// ведёт себя как обычный View.
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Typography, Button } from '../ui';
import { useTheme } from '../../context/ThemeContext';
import { useI18n } from '../../i18n';
import { fontWeights } from '../../styles/typographyTokens';
import type { JournalEntryDraft, JournalPrompts } from '../../types/auth';
import { cleanDraft, hasContent } from './reflectionFlow';
import { ReflectionFields } from './ReflectionFields';

import { ACCENT_TEXT } from '../../styles/accentText';
import { dangerColors } from './reflectionColors';
interface Props {
  visible: boolean;
  prompts: JournalPrompts;
  practiceCompletionId?: number | null;
  onClose: () => void;
  onSave: (draft: JournalEntryDraft) => Promise<void>;
  /**
   * CORE-1751 — «Мои записи» после сохранения. Экран сам решает, куда вести:
   * окно не знает навигации и не должно.
   */
  onOpenJournal?: () => void;
}

/**
 * CORE-1455 — окно рефлексии после практики.
 *
 * Вопросы приходят с сервера и здесь не продублированы: список правится
 * продуктом без выката приложений, и второй его экземпляр в коде разошёлся бы
 * с ним молча. Порядок полей — тот, в котором вопросы пришли.
 *
 * Ни одно поле не обязательно. Это не форма, а место, где можно остановиться:
 * человек отвечает на один вопрос из пяти или пишет в свободной форме, и это
 * нормальный, а не ущербный сценарий. Кнопка сохранения включается, когда есть
 * хоть одно слово, — и это единственное ограничение.
 *
 * Закрыть можно в любой момент. Практика уже пройдена; удерживать человека в
 * окне рефлексии значило бы превращать её в обязанность.
 *
 * CORE-1751 — после сохранения окно не исчезает молча, а говорит, что запись
 * сохранена и где её найти. Раньше написанное уходило в никуда: перечитать его
 * было негде.
 */
export function ReflectionModal({
  visible, prompts, practiceCompletionId, onClose, onSave, onOpenJournal,
}: Props) {
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const { colors, isDark } = useTheme();
  const { t } = useI18n('dashboard');
  const [draft, setDraft] = useState<JournalEntryDraft>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave = useMemo(() => hasContent(draft) && !saving, [draft, saving]);

  // Окно остаётся смонтированным между показами (экран держит его, пока есть
  // вопросы), поэтому каждое новое открытие начинается с чистого листа: иначе
  // следующая практика открылась бы сразу на «Запись сохранена».
  useEffect(() => {
    if (!visible) return;
    setDraft({});
    setSaved(false);
    setError(null);
  }, [visible]);

  if (!visible) return null;

  const setField = (field: string, value: string) =>
    setDraft((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(cleanDraft({ ...draft, practiceCompletionId }));
      setSaved(true);
    } catch {
      // Текст человека остаётся на экране: потерять написанное о себе хуже,
      // чем показать ошибку. Тот же довод, что в CORE-1350 П3.
      setError(t('journal.reflection.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal visible transparent animationType="slide" onRequestClose={onClose}>
      <KeyboardAvoidingView style={styles.fill} behavior="padding">
        <View style={styles.backdrop}>
          <ScrollView
            style={styles.scroll}
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="handled"
          >
            {/* CORE-1751 — фон без альфы (`surfaceDialog`, CORE-1133): на полупрозрачном
                surfaceHover сквозь карточку читались плеер и название практики. */}
            <View style={[styles.card, isDesktop && styles.cardDesktop, { backgroundColor: colors.surfaceDialog }]}>
              <View style={styles.header}>
                <Typography variant="h3" style={{ color: colors.textPrimary }}>
                  {t('journal.reflection.title')}
                </Typography>
                <Pressable
                  onPress={onClose}
                  testID="reflection-close"
                  hitSlop={12}
                  accessibilityRole="button"
                  accessibilityLabel={t('journal.reflection.close')}
                >
                  <Ionicons name="close" size={22} color={colors.textSecondary} />
                </Pressable>
              </View>

              {saved ? (
                <View testID="reflection-saved" style={styles.saved}>
                  <View style={styles.savedIcon}>
                    <Ionicons name="checkmark" size={24} color={ACCENT_TEXT} />
                  </View>
                  <Typography variant="body" style={[styles.savedTitle, { color: colors.textPrimary }]}>
                    {t('journal.reflection.savedTitle')}
                  </Typography>
                  <Typography variant="caption" style={[styles.hint, { color: colors.textSecondary }]}>
                    {t('journal.reflection.savedText')}
                  </Typography>
                  {onOpenJournal ? (
                    <Button
                      testID="reflection-open-journal"
                      variant="convex-fire"
                      onPress={onOpenJournal}
                      style={styles.save}
                    >
                      {t('journal.reflection.openJournal')}
                    </Button>
                  ) : null}
                  <Button
                    testID="reflection-done"
                    variant="secondary"
                    onPress={onClose}
                    style={styles.save}
                  >
                    {t('journal.reflection.done')}
                  </Button>
                </View>
              ) : (
                <>
                  <Typography variant="caption" style={[styles.hint, { color: colors.textSecondary }]}>
                    {t('journal.reflection.hint')}
                  </Typography>

                  <ReflectionFields prompts={prompts} draft={draft} onChange={setField} />

                  {error ? (
                    <Typography variant="caption" style={[styles.error, { color: dangerColors(colors.surfaceDialog, isDark).ink }]}>{error}</Typography>
                  ) : null}

                  <Button
                    testID="reflection-save"
                    variant="convex-fire"
                    onPress={handleSave}
                    disabled={!canSave}
                    isLoading={saving}
                    style={styles.save}
                  >
                    {t('journal.reflection.save')}
                  </Button>
                </>
              )}
            </View>
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  fill: { flex: 1 },
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)' },
  scroll: { flex: 1 },
  scrollContent: { flexGrow: 1, justifyContent: 'center', padding: 16 },
  card: { borderRadius: 20, padding: 20, gap: 14 },
  cardDesktop: { maxWidth: 520, alignSelf: 'center', width: '100%' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  hint: { lineHeight: 18 },
  error: { lineHeight: 18 },
  save: { width: '100%', marginTop: 4 },
  saved: { gap: 10, alignItems: 'stretch' },
  savedIcon: {
    width: 48, height: 48, borderRadius: 24, alignItems: 'center', justifyContent: 'center',
    backgroundColor: 'rgba(233,130,69,0.08)',
  },
  savedTitle: { fontWeight: fontWeights.semibold },
});
